import React, { useState, useEffect } from 'react';
import { updateUserProfile } from '../services/userService';
import { loadAuthUser, saveAuthUser } from '../services/auth';
import { getUserStorageKey, migrateLegacyData } from '../utils/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import { logEvent } from '../services/analytics';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PageLayout } from '../components/PageLayout';
import { getPageBackgroundGradientStyle } from '../config/theme';
// ==================== 类型定义 ====================

const Onboarding: React.FC = () => {
  // ==================== Hooks 和状态管理 ====================
  const { t, language, setLanguage, theme } = useAppSettings(); // 国际化翻译函数和语言设置
  const navigate = useNavigate(); // 路由导航函数
  const location = useLocation();

  /**
   * 步骤状态（共 8 步）：
   * 1-4：新增的功能介绍页（与截图一致）
   * 5：语言选择
   * 6：姓名输入
   * 7：意图选择（最多选 3 个）
   * 8：语音选择
   */
  const [step, setStep] = useState(1);

  const INTRO_STEP_COUNT = 6;
  const SETUP_STEP_COUNT = 5; // language -> name -> intent -> voice -> ai consent
  const TOTAL_STEPS = INTRO_STEP_COUNT + SETUP_STEP_COUNT;
  const isIntroStep = step <= INTRO_STEP_COUNT;
  const setupStep = step - INTRO_STEP_COUNT; // 1..5 for steps 7..11
  const setupProgress = Math.min(Math.max(setupStep, 1), SETUP_STEP_COUNT) / SETUP_STEP_COUNT;

  // Intro swipe state
  const introStartX = React.useRef<number | null>(null);
  const introDeltaX = React.useRef(0);
  const introMouseDown = React.useRef(false);

  type RecommendedPersonaKey = 'cheerleader' | 'listener' | 'active_advisor' | 'sleep_companion';

  const introSlides = [
    {
      titleKey: 'onboarding.intro.realTime.title',
      subtitleKey: 'onboarding.intro.realTime.subtitle',
      image: '/images/onboarding_conversation.png',
    },
    {
      titleKey: 'onboarding.intro.supported.title',
      subtitleKey: 'onboarding.intro.supported.subtitle',
      image: '/images/onboarding/feel_supported.png',
    },
    {
      titleKey: 'onboarding.intro.understand.title',
      subtitleKey: 'onboarding.intro.understand.subtitle',
      image: '/images/onboarding/understand_yourself.png',
    },
    {
      titleKey: 'onboarding.intro.makeTick.title',
      subtitleKey: 'onboarding.intro.makeTick.subtitle',
      image: '/images/onboarding/make_trick.png',
    },
    {
      titleKey: 'onboarding.intro.catchMoment.title',
      subtitleKey: 'onboarding.intro.catchMoment.subtitle',
      image: '/images/onboarding/catch_moment.png',
    },
    {
      titleKey: 'onboarding.intro.private.title',
      subtitleKey: 'onboarding.intro.private.subtitle',
      image: '/images/onboarding_lock.png',
    },
  ] as const;

  const applyIntroSwipe = (deltaX: number) => {
    const threshold = 50;
    if (Math.abs(deltaX) <= threshold) return;

    // swipe left -> next
    if (deltaX < 0 && step < INTRO_STEP_COUNT + 1) {
      // 1..4 -> move forward (4 can go to 5)
      setStep(step + 1);
    }
    // swipe right -> prev (only within intro)
    if (deltaX > 0 && step > 1 && step <= INTRO_STEP_COUNT) {
      setStep(step - 1);
    }
  };

  const handleIntroTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    introStartX.current = e.touches[0].clientX;
    introDeltaX.current = 0;
  };

  const handleIntroTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (introStartX.current === null) return;
    introDeltaX.current = e.touches[0].clientX - introStartX.current;
  };

  const handleIntroTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
    applyIntroSwipe(introDeltaX.current);
    introStartX.current = null;
    introDeltaX.current = 0;
  };

  // Desktop preview: mouse drag to simulate swipe
  const handleIntroMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    introMouseDown.current = true;
    introStartX.current = e.clientX;
    introDeltaX.current = 0;
  };

  const handleIntroMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!introMouseDown.current || introStartX.current === null) return;
    introDeltaX.current = e.clientX - introStartX.current;
  };

  const handleIntroMouseUpOrLeave: React.MouseEventHandler<HTMLDivElement> = () => {
    if (!introMouseDown.current) return;
    introMouseDown.current = false;
    applyIntroSwipe(introDeltaX.current);
    introStartX.current = null;
    introDeltaX.current = 0;
  };

  // Desktop preview: keyboard arrows
  const handleIntroKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (step < INTRO_STEP_COUNT + 1) setStep(step + 1);
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (step > 1 && step <= INTRO_STEP_COUNT) setStep(step - 1);
    }
  };
  
  // Track Onboarding Start
  useEffect(() => {
      logEvent('onboarding_start');
  }, []);

  // 用户输入的姓名（Apple Sign-In 可能带回 prefillName）
  const [name, setName] = useState<string>(() => {
    const prefill = (location.state as any)?.prefillName;
    return typeof prefill === 'string' ? prefill : '';
  });
  // 用户选择的语言（第二步中的选项）
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language || 'en-US');
  // 用户选择的意图（最多选择三个）
  const [selectedIntents, setSelectedIntents] = useState<string[]>([]);
  // 用户选择的语音（第三步中的选项）
  const [selectedVoice, setSelectedVoice] = useState<string | null>(null);

  // 检查是否已完成引导，如果已完成则直接跳转到主页面
  useEffect(() => {
    const user = loadAuthUser();
    
    const completedKey = getUserStorageKey('onboarding-completed', user?.id);

    if (user?.id) {
       migrateLegacyData('onboarding-completed', user.id);
       migrateLegacyData('onboarding-data', user.id);
    }

    const onboardingCompleted = localStorage.getItem(completedKey);
    if (onboardingCompleted) {
      navigate('/whisper', { replace: true });
    }
  }, [navigate]);

  // ==================== 数据配置 ====================
  // 语言选项数据（第二步显示的语言选项）
  const languageOptions = [
    { code: 'en-US', name: 'English' },
    { code: 'es-ES', name: 'Spanish' },
  ];

  const intentOptions = [
    { id: 'unpack', emoji: '🧠', labelKey: 'onboarding.intent.unpack' },
    { id: 'recharge', emoji: '🔋', labelKey: 'onboarding.intent.recharge' },
    { id: 'process', emoji: '🌊', labelKey: 'onboarding.intent.process' },
    { id: 'calm', emoji: '🕯️', labelKey: 'onboarding.intent.calm' },
    { id: 'sleep', emoji: '😴', labelKey: 'onboarding.intent.sleep' },
    { id: 'talk', emoji: '💬', labelKey: 'onboarding.intent.talk' },
  ] as const;

  const defaultRecommendedPersonaKey = React.useMemo<RecommendedPersonaKey>(() => {
    // Strong rule: selected sleep intent => Sleep Companion wins
    if (selectedIntents.includes('sleep')) return 'sleep_companion';

    const scores: Record<RecommendedPersonaKey, number> = {
      cheerleader: 0,
      listener: 0,
      active_advisor: 0,
      sleep_companion: 0,
    };

    for (const intent of selectedIntents) {
      switch (intent) {
        case 'unpack': {
          scores.cheerleader += 2;
          scores.listener += 1;
          break;
        }
        case 'recharge': {
          scores.listener += 2;
          scores.cheerleader += 1;
          break;
        }
        case 'process': {
          scores.active_advisor += 2;
          scores.listener += 1;
          break;
        }
        case 'calm': {
          scores.sleep_companion += 2;
          scores.listener += 1;
          break;
        }
        case 'talk': {
          scores.listener += 2;
          scores.cheerleader += 1;
          break;
        }
        default:
          break;
      }
    }

    const maxScore = Math.max(...Object.values(scores));
    const winners = (Object.keys(scores) as RecommendedPersonaKey[]).filter((k) => scores[k] === maxScore);
    return winners[Math.floor(Math.random() * winners.length)];
  }, [selectedIntents]);

  const personaByKey = React.useMemo(() => {
    const byKey: Record<RecommendedPersonaKey, {
      key: RecommendedPersonaKey;
      // Underlying personaId saved as `voice` for rest of app
      personaId: RecommendedPersonaKey;
      titleKey: string;
      descKey: string;
      gradient: string;
    }> = {
      cheerleader: {
        key: 'cheerleader',
        personaId: 'cheerleader',
        titleKey: 'onboarding.reco.persona.cheerleader.title',
        descKey: 'onboarding.reco.persona.cheerleader.desc',
        gradient: 'linear-gradient(180deg, rgba(255, 105, 180, 0.35) 0%, rgba(255, 195, 160, 0.25) 100%)',
      },
      listener: {
        key: 'listener',
        personaId: 'listener',
        titleKey: 'onboarding.reco.persona.listener.title',
        descKey: 'onboarding.reco.persona.listener.desc',
        gradient: 'linear-gradient(180deg, rgba(216, 112, 196, 0.35) 0%, rgba(147, 51, 234, 0.18) 100%)',
      },
      active_advisor: {
        key: 'active_advisor',
        personaId: 'active_advisor',
        titleKey: 'onboarding.reco.persona.activeAdvisor.title',
        descKey: 'onboarding.reco.persona.activeAdvisor.desc',
        gradient: 'linear-gradient(180deg, rgba(34, 197, 94, 0.35) 0%, rgba(16, 185, 129, 0.18) 100%)',
      },
      sleep_companion: {
        key: 'sleep_companion',
        personaId: 'sleep_companion',
        titleKey: 'onboarding.reco.persona.sleepCompanion.title',
        descKey: 'onboarding.reco.persona.sleepCompanion.desc',
        gradient: 'linear-gradient(180deg, rgba(59, 130, 246, 0.38) 0%, rgba(37, 99, 235, 0.18) 100%)',
      },
    };
    return byKey;
  }, []);

  const personaOrder: RecommendedPersonaKey[] = ['cheerleader', 'listener', 'active_advisor', 'sleep_companion'];
  const [displayPersonaKey, setDisplayPersonaKey] = useState<RecommendedPersonaKey>('listener');

  // Entering the final step: default to the recommended persona, but allow manual switching
  useEffect(() => {
    if (step === TOTAL_STEPS) {
      setDisplayPersonaKey(defaultRecommendedPersonaKey);
    }
  }, [step, TOTAL_STEPS, defaultRecommendedPersonaKey]);

  const displayedPersona = personaByKey[displayPersonaKey];

  const goPrevPersona = () => {
    setDisplayPersonaKey((prev) => {
      const idx = personaOrder.indexOf(prev);
      const nextIdx = (idx - 1 + personaOrder.length) % personaOrder.length;
      return personaOrder[nextIdx];
    });
  };

  const goNextPersona = () => {
    setDisplayPersonaKey((prev) => {
      const idx = personaOrder.indexOf(prev);
      const nextIdx = (idx + 1) % personaOrder.length;
      return personaOrder[nextIdx];
    });
  };

  // When reaching the final step, auto-set the recommended persona (no manual selection)
  useEffect(() => {
    if (step === TOTAL_STEPS) {
      setSelectedVoice(displayedPersona.personaId);
    }
  }, [step, TOTAL_STEPS, displayedPersona.personaId]);

  const toggleIntent = (intentId: string) => {
    setSelectedIntents((prev) => {
      if (prev.includes(intentId)) return prev.filter((x) => x !== intentId);
      if (prev.length >= 3) return prev;
      return [...prev, intentId];
    });
  };

  // ==================== 事件处理函数 ====================
  // 保存当前进度（即时保存，防止中断）
  const saveProgress = () => {
    try {
      const userData = {
        name,
        voice: selectedVoice, // 当前已选的 voice
        intents: selectedIntents,
        // Language 已经在 setLanguage 时保存了，这里也可以冗余存一下，但主要为了恢复 name
      };
      const user = loadAuthUser();
      localStorage.setItem(getUserStorageKey('onboarding-data', user?.id), JSON.stringify(userData));
      console.log('Saved onboarding progress:', userData);
    } catch (error) {
      console.error('Failed to save onboarding progress:', error);
    }
  };

  // 处理"下一步"按钮点击
  const handleNext = () => {
    // Analytics: Track Step Completion
    logEvent('onboarding_step_complete', { step: step });

    // 每次点击下一步都保存当前输入 (特别是 Name)
    saveProgress();
    
    if (step < TOTAL_STEPS) {
      // 如果不是最后一步，进入下一步
      // 语言选择（第 5 步）时，保存语言设置
      if (step === INTRO_STEP_COUNT + 1) {
        setLanguage(selectedLanguage);
      }
      setStep(step + 1);
      return;
    }
    // 最后一步：完成并跳转
    handleComplete();
  };

  // 处理完成/跳过按钮点击
  const handleComplete = () => {
    // Analytics: Track Completion
    logEvent('onboarding_complete', { 
        skipped: step < TOTAL_STEPS, // if early skip
        final_step: step
    });

    const finalVoice = step === TOTAL_STEPS ? (selectedVoice || displayedPersona.personaId) : selectedVoice;

    // 最后一步（AI 同意页）完成时，保存同意状态
    if (step === TOTAL_STEPS) {
      localStorage.setItem('ai-data-consent-v1', '1');
    }

    // 如果第三步选择了语音，直接保存并跳转
    if (step === TOTAL_STEPS && finalVoice) {
      saveAndNavigate(finalVoice);
    } else {
      // 否则（跳过场景）：只保存当前进度，即使不完整也跳转
      // 这样用户可以选择跳过某些步骤
      try {
        const userData = {
          name,
          voice: finalVoice,
          intents: selectedIntents,
        };
        const user = loadAuthUser();
        localStorage.setItem(getUserStorageKey('onboarding-data', user?.id), JSON.stringify(userData));
        localStorage.setItem(getUserStorageKey('onboarding-completed', user?.id), '1');
        if (finalVoice) {
          localStorage.setItem(getUserStorageKey('t2s_persona_style', user?.id), finalVoice);
        }
      } catch (error) {
        console.error('Failed to save onboarding data:', error);
      }
      navigate('/whisper', { replace: true });
    }
  };

  // 保存用户数据并跳转到主页
  // 使用 useCallback 优化性能，避免不必要的重新渲染
  const saveAndNavigate = React.useCallback(async (voiceId: string) => {
    // 保存用户数据到本地存储
    try {
      const userData = {
        name,
        voice: voiceId,
        intents: selectedIntents,
      };
      const user = loadAuthUser();
      localStorage.setItem(getUserStorageKey('onboarding-data', user?.id), JSON.stringify(userData));
      localStorage.setItem(getUserStorageKey('onboarding-completed', user?.id), '1'); // 标记引导流程已完成
      if (voiceId) {
        localStorage.setItem(getUserStorageKey('t2s_persona_style', user?.id), voiceId);
      }
    } catch (error) {
      console.error('Failed to save onboarding data:', error);
    }
    
    // Sync nickname to backend and update local auth user
    // We make this an inner function to keep logic clean, but we will await it.
    const syncProfile = async () => {
        if (name) {
            try {
                const updatedProfile = await updateUserProfile({ nickname: name });
                console.log('Nickname synced to backend:', updatedProfile);
                
                // Update local auth user cache
                const currentUser = loadAuthUser();
                if (currentUser) {
                    const updatedUser = { 
                        ...currentUser, 
                        nickname: updatedProfile.nickname || name,
                    };
                    saveAuthUser(updatedUser);
                    console.log('Local auth user updated with nickname');
                    
                    // Set the persona style so Whisper page picks it up immediately
                    if (voiceId) {
                      localStorage.setItem(getUserStorageKey("t2s_persona_style", currentUser.id), voiceId);
                    }
                    
                    // Clear pending data since we successfully synced
                    localStorage.removeItem(getUserStorageKey('onboarding-data', currentUser.id));
                }
            } catch (err: any) {
                console.error('Failed to sync nickname to backend:', err);
            }
        }
    };

    // Execute sync (await it to ensure we save state before leaving)
    // Note: We might want to show a loading spinner here? 
    // For now, we just await. If it takes too long, users might think it stuck.
    // But nickname update should be fast.
    await syncProfile();

    // 跳转到订阅页面
    navigate('/whisper', { replace: true });
  }, [name, selectedIntents, navigate]);

  // 检查当前步骤是否可以继续
  // 用于控制"下一步"按钮的启用/禁用状态
  const canProceed = () => {
    // 1-4：介绍页，始终可继续
    if (isIntroStep) return true;
    // 第 5 步：语言选择
    if (step === INTRO_STEP_COUNT + 1) return selectedLanguage !== null;
    // 第 6 步：姓名输入
    if (step === INTRO_STEP_COUNT + 2) return name.trim().length > 0;
    // 第 7 步：意图选择（至少选 1 个）
    if (step === INTRO_STEP_COUNT + 3) return selectedIntents.length > 0;
    // 第 8 步：语音选择（displayedPersona 始终有默认值，无需强制手动选）
    if (step === INTRO_STEP_COUNT + 4) return true;
    // 第 9 步（最后一步）：AI 数据同意页，始终可继续（按钮本身即"同意"语义）
    if (step === INTRO_STEP_COUNT + 5) return true;
    return false;
  };


  // ==================== 渲染函数 ====================
  return (
    <PageLayout
      backgroundStyle={{
        ...getPageBackgroundGradientStyle(theme)
      }}
      // 仅「意图选择」页需要支持纵向滚动（如果内容显示不全）
      scrollable={step === INTRO_STEP_COUNT + 3}
      contentStyle={isIntroStep ? ({ touchAction: 'pan-x' } as React.CSSProperties) : undefined}
      header={
        <div className="px-6 pt-24 pb-8 flex items-center justify-center">
          {isIntroStep ? (
            // 前 4 页：独立 onboarding，导航条为 4 段
            <div className="flex gap-2.5 items-center">
              {Array.from({ length: INTRO_STEP_COUNT }, (_, idx) => idx + 1).map((i) => (
                <div
                  key={i}
                  className={`h-1.5 w-10 rounded-full transition-all ${i === step
                    ? 'bg-blue-300'
                    : i < step
                      ? 'bg-white/30'
                      : 'bg-white/20'
                    }`}
                />
              ))}
            </div>
          ) : (
            // 姓名页之后：导航条风格变化为“单条进度条”
            <div className="w-[82%] max-w-sm h-2 rounded-full bg-white/25 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-400 transition-all"
                style={{ width: `${Math.round(setupProgress * 100)}%` }}
              />
            </div>
          )}
        </div>
      }
      footer={
        <>
          {/* Intro 页无按钮；从设置阶段开始按设计稿只保留一个 Continue 按钮 */}
          {!isIntroStep && step >= INTRO_STEP_COUNT + 1 && step <= TOTAL_STEPS ? (
            <div className="px-8 pb-10 space-y-3">
              <button
                onClick={step === TOTAL_STEPS ? handleComplete : handleNext}
                disabled={step === TOTAL_STEPS ? false : !canProceed()}
                className={`w-full py-4 rounded-full font-semibold text-lg transition-all ${canProceed()
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
              >
                {step === TOTAL_STEPS ? t('aiConsent.agree') : step === INTRO_STEP_COUNT + 4 ? t('common.continue') : t('common.continue')}
              </button>
              {/* Skip 按钮：仅在姓名步骤显示，允许用户跳过不填名字 */}
              {step === INTRO_STEP_COUNT + 2 && (
                <button
                  onClick={() => {
                    setName('');
                    handleNext();
                  }}
                  className="w-full py-3 rounded-full font-medium text-base text-white/60 transition-all hover:text-white/90"
                >
                  {t('common.skip')}
                </button>
              )}
            </div>
          ) : null}
        </>
      }
    >
      {/* ========== 介绍页（第 1-4 步）：仅左右滑动切换 ========== */}
      {isIntroStep ? (
        <div
          className="h-full w-full overflow-hidden overscroll-none"
          onTouchStart={handleIntroTouchStart}
          onTouchMove={handleIntroTouchMove}
          onTouchEnd={handleIntroTouchEnd}
          onMouseDown={handleIntroMouseDown}
          onMouseMove={handleIntroMouseMove}
          onMouseUp={handleIntroMouseUpOrLeave}
          onMouseLeave={handleIntroMouseUpOrLeave}
          onKeyDown={handleIntroKeyDown}
          tabIndex={0}
        >
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${(step - 1) * 100}%)` }}
          >
            {introSlides.map((s) => (
              <section key={s.image} className="min-w-full h-full relative overflow-hidden px-6">
                {/* 文案区域（顶部） */}
                <div className="pt-4 flex flex-col items-center text-center">
                  <div className="max-w-sm mx-auto">
                    <h1 className="text-2xl font-semibold text-white whitespace-pre-line leading-tight">
                      {t(s.titleKey)}
                    </h1>
                    <p className="mt-4 text-white/85 text-base font-light leading-relaxed whitespace-pre-line">
                      {t(s.subtitleKey)}
                    </p>
                  </div>
                </div>

                {/* 图片作为背景主视觉（底部），按比例展示，不参与布局高度，避免纵向滚动 */}
                {s.image === '/images/onboarding_lock.png' ? (
                  // 第 4 页：锁头图需要更大，宽度同屏宽
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-screen flex justify-center pointer-events-none select-none">
                    <img
                      src={s.image}
                      alt={t(s.titleKey)}
                      className="block max-w-none"
                      style={{
                        width: '100vw',
                        height: 'auto',
                      }}
                    />
                  </div>
                ) : s.image === '/images/onboarding/feel_supported.png' ? (
                  // 第 2 页：深蓝色背景面板（60% 宽，延伸到底部）+ 图片（全屏宽，叠于其上）
                  <>
                    {/* 深蓝背景面板：居中，60% 宽，顶部圆角，延伸到底部 */}
                    <div
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none select-none"
                      style={{
                        width: '72%',
                        top: '45%',
                        borderRadius: '20px 20px 0 0',
                        background: 'rgba(7, 17, 72, 0.54)',
                        boxShadow: '0 -4px 30px rgba(7, 17, 100, 0.35)',
                      }}
                    />
                    {/* 图片：全屏宽，叠在背景面板之上 */}
                    <div
                      className="absolute inset-x-0 pointer-events-none select-none"
                      style={{ bottom: '6vh' }}
                    >
                      <img
                        src={s.image}
                        alt={t(s.titleKey)}
                        className="w-full"
                        style={{ display: 'block' }}
                      />
                    </div>
                  </>
                ) : (
                  // 其余页保持不变
                  <div className="absolute inset-x-0 bottom-0 flex justify-center pointer-events-none select-none">
                    <img
                      src={s.image}
                      alt={t(s.titleKey)}
                      className="w-[92%] max-w-sm"
                      style={{ maxHeight: '62vh', objectFit: 'contain' }}
                    />
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>
      ) : (
        <div className="px-6 py-6">

        {/* ========== 第五步：语言选择（先语言，再姓名） ========== */}
        {step === INTRO_STEP_COUNT + 1 && (
          <div className="pt-4 flex flex-col items-center">
            <div className="w-full max-w-sm">
              <h1 className="text-2xl font-semibold text-white mb-10 text-left">
                {t('onboarding.step2.title')}
              </h1>
              {/* 语言选项列表：胶囊按钮 */}
              <div className="space-y-4">
                {languageOptions.map((lang) => {
                  const selected = selectedLanguage === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => setSelectedLanguage(lang.code)}
                      className={`w-full px-6 py-4 rounded-full border transition-all text-left ${selected
                        ? 'bg-white/10 border-white/60'
                        : 'bg-transparent border-white/35 hover:bg-white/5'
                        }`}
                    >
                      <span className="text-white text-sm font-medium">
                        {lang.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ========== 第六步：姓名输入 ========== */}
        {step === INTRO_STEP_COUNT + 2 && (
          <div className="pt-4 flex flex-col items-center">
            <div className="w-full max-w-sm">
              <h1 className="text-2xl font-semibold text-white mb-10 text-left">
                {t('onboarding.step1.title')}
              </h1>
              {/* 姓名输入框：支持回车键快速提交 */}
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  const value = e.target.value;
                  // 限制最大长度为30个字符
                  if (value.length <= 30) {
                    setName(value);
                  }
                }}
                onKeyPress={(e) => {
                  // 按回车键可以直接进入下一步（如果已输入姓名）
                  if (e.key === 'Enter' && canProceed()) {
                    handleNext();
                  }
                }}
                placeholder={t('onboarding.step1.placeholder')}
                className="w-full px-6 py-4 rounded-full bg-transparent border border-white/40 text-white placeholder:text-white/55 focus:outline-none focus:border-white/60 transition-colors text-base"
                autoFocus // 页面加载时自动聚焦
                maxLength={30}
              />
            </div>
          </div>
        )}

        {/* ========== 第七步：意图选择（最多选 3 个） ========== */}
        {step === INTRO_STEP_COUNT + 3 && (
          <div className="pt-4 flex flex-col items-center">
            <div className="w-full max-w-sm">
              <h1 className="text-2xl font-semibold text-white mb-2 text-left">
                {t('onboarding.intent.title')}
              </h1>
              <p className="text-white/75 text-sm mb-8 text-left">
                {t('onboarding.intent.subtitle')}
              </p>

              <div className="space-y-3 pb-6">
                {intentOptions.map((opt) => {
                  const selected = selectedIntents.includes(opt.id);
                  const disabled = !selected && selectedIntents.length >= 3;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleIntent(opt.id)}
                      className={`w-full px-5 py-3 rounded-full border transition-all text-left ${selected
                        ? 'bg-white/10 border-white/60'
                        : 'bg-transparent border-white/35 hover:bg-white/5'
                        } ${disabled ? 'opacity-60' : ''}`}
                    >
                      <span className="text-white text-xs font-medium">
                        {opt.emoji} {t(opt.labelKey)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ========== 第八步：语音推荐（无需手动选择） ========== */}
        {step === INTRO_STEP_COUNT + 4 && (
          <div className="pt-4 flex flex-col items-center">
            {/* Narrower card width to match design (larger side margins) */}
            <div className="w-full max-w-[300px]">
              <h1 className="text-2xl font-semibold text-white mb-10 text-center">
                {t('onboarding.reco.title')}
              </h1>

              <div
                className="rounded-3xl px-6 py-6 border border-white/20 flex flex-col mb-8"
                style={{
                  background: displayedPersona.gradient,
                  backdropFilter: 'blur(20px) saturate(110%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(110%)',
                  // Ensure full rounded corners are visible on short screens
                  minHeight: '340px',
                  height: '52vh',
                  maxHeight: '420px',
                }}
              >
                <div className="text-white/80 text-xs font-medium mb-6 text-center">
                  {t('onboarding.reco.yourLumi')}
                </div>

                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="w-full flex items-center justify-between">
                    <button
                      type="button"
                      onClick={goPrevPersona}
                      className="p-2 text-white/90 hover:text-white transition-colors"
                      aria-label="previous-persona"
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </button>

                    <div className="text-white text-3xl font-semibold text-center leading-tight px-2">
                      {t(displayedPersona.titleKey)}
                    </div>

                    <button
                      type="button"
                      onClick={goNextPersona}
                      className="p-2 text-white/90 hover:text-white transition-colors"
                      aria-label="next-persona"
                    >
                      <ChevronRight className="w-8 h-8" />
                    </button>
                  </div>

                  <div className="mt-4 text-white/80 text-sm text-center max-w-[280px]">
                    {t(displayedPersona.descKey)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ========== 第九步（最后一步）：AI 数据使用同意 ========== */}
        {step === INTRO_STEP_COUNT + 5 && (
          <div className="pt-4 flex flex-col items-center px-2">
            <div className="w-full max-w-sm flex flex-col gap-6">
              <h1 className="text-2xl font-semibold text-white text-center leading-snug">
                {t('aiConsent.title')}
              </h1>
              <p className="text-base text-white/75 leading-relaxed text-center">
                {t('aiConsent.body')}
              </p>
              <a
                href="https://pli477.github.io/apple_policy_website/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-300 underline underline-offset-2 text-center hover:text-blue-200 transition-colors"
              >
                {t('aiConsent.privacyLink')}
              </a>
            </div>
          </div>
        )}
        </div>
      )}
    </PageLayout >
  );
};

export default Onboarding;

